import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { auth, firestore } from '../firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import LoadingSpinner from './LoadingSpinner';

const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    console.log('ProtectedRoute: Checking authentication...', { pathname: window.location.pathname });
    
    // Check for custom authentication first
    const customUser = localStorage.getItem('customUser');
    const customRole = localStorage.getItem('userRole');
    
    console.log('ProtectedRoute: Custom auth check:', { customUser: !!customUser, customRole });
    
    if (customUser && customRole) {
      const parsedUser = JSON.parse(customUser);
      console.log('ProtectedRoute: Using custom auth:', parsedUser);
      setUser(parsedUser);
      setUserRole(customRole);
      setLoading(false);
      return;
    }
    
    // Fallback to Firebase Auth
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('ProtectedRoute: Firebase auth check:', { user: !!user });
      setUser(user);
      if (user) {
        try {
          const userRef = doc(firestore, 'users', user.uid);
          const snap = await getDoc(userRef);
          if (snap.exists()) {
            const role = snap.data().role;
            console.log('ProtectedRoute: User role:', role);
            setUserRole(role);
          }
        } catch (error) {
          console.error('Error fetching user role:', error);
        }
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // If specific roles are required and user doesn't have the required role
  console.log('ProtectedRoute: Checking role access:', { userRole, allowedRoles, hasAccess: allowedRoles.includes(userRole) });
  
  // If no specific roles are required, allow access
  if (allowedRoles.length === 0) {
    console.log('ProtectedRoute: No role restrictions - allowing access');
    return children;
  }
  
  // Check if user has required role
  if (allowedRoles.includes(userRole)) {
    console.log('ProtectedRoute: User has required role - allowing access');
    return children;
  }
  
  // User doesn't have required role - redirect based on their actual role
  console.log('ProtectedRoute: Role mismatch - redirecting user to appropriate dashboard');
  if (userRole === 'admin' || userRole === 'manager') {
    return <Navigate to="/admin" replace />;
  } else if (userRole === 'student') {
    return <Navigate to="/student-dashboard" replace />;
  } else if (userRole === 'employee') {
    return <Navigate to="/employee-dashboard" replace />;
  } else {
    console.log('ProtectedRoute: Unknown role - redirecting to login');
    return <Navigate to="/login" replace />;
  }

  return children;
};

export default ProtectedRoute;